// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/mypaintTranslate.js
//
// MyPaint brushes (.myb) are a procedural round dab whose every setting is
// base_value + sum(input mappings). Inputs map through piecewise-linear
// curves in input units (libmypaint). Version 3 files are JSON; version 2
// files are "setting base | input (x y), (x y)" text lines.
//
// Translation evaluates the dynamics at the inputs Voxemera has (pressure,
// random, tilt) and bakes them into Voxemera curves:
//   radius_logarithmic + pressure   size response: exp(f(p)) / exp(f(1))
//   opaque * opaque_multiply        opacity per dab (build-up)
//   hardness                        procedural tip edge
//   dabs_per_actual_radius/_basic   spacing = 0.5 / (actual + basic) of
//                                   the diameter
//   offset_by_random                scatter (radius units -> diameter / 2)
//   radius_by_random                size jitter
//   elliptical_dab_ratio/_angle     roundness and fixed angle
//   change_color_h/l/hsv_s + random hue/lightness/saturation jitter
// Smudge, colour pickup, lock-alpha, colorize, posterize, slow tracking
// and custom inputs are reported as missing.
import { scoreCompatibility } from './brushSchema.js';

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function parseMyPaintBrush(text) {
  const source = String(text || '').replace(/^﻿/, '');
  if (source.length > 2 * 1024 * 1024) throw new Error('MyPaint brush file is too large.');
  const trimmed = source.trimStart();
  if (trimmed.startsWith('{')) {
    const json = JSON.parse(trimmed);
    const settings = {};
    const rawSettings = json && typeof json.settings === 'object' ? json.settings : {};
    Object.keys(rawSettings).slice(0, 200).forEach((key) => {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') return;
      const entry = rawSettings[key] || {};
      const inputs = {};
      Object.keys(entry.inputs || {}).slice(0, 32).forEach((input) => {
        const points = Array.isArray(entry.inputs[input]) ? entry.inputs[input] : [];
        inputs[input] = points.slice(0, 64).map((p) => [num(p && p[0]), num(p && p[1])]);
      });
      settings[key] = { base: num(entry.base_value), inputs };
    });
    return { version: num(json.version, 3), name: String(json.comment || '').slice(0, 200), parent: String(json.parent_brush_name || ''), settings };
  }
  const settings = {};
  let version = 2;
  source.split(/\r?\n/).slice(0, 2000).forEach((line) => {
    const clean = line.trim();
    if (!clean || clean.startsWith('#')) return;
    const versionMatch = clean.match(/^version\s+(\d+)/);
    if (versionMatch) {
      version = Number(versionMatch[1]);
      return;
    }
    const [head, ...inputParts] = clean.split('|');
    const headMatch = head.trim().match(/^([a-z_0-9]+)\s+(-?[\d.eE+-]+)/);
    if (!headMatch) return;
    const inputs = {};
    inputParts.forEach((part) => {
      const m = part.trim().match(/^([a-z_0-9]+)\s+(.*)$/);
      if (!m) return;
      const points = [];
      m[2].replace(/\(\s*(-?[\d.eE+-]+)\s+(-?[\d.eE+-]+)\s*\)/g, (s, x, y) => {
        points.push([Number(x), Number(y)]);
        return s;
      });
      if (points.length) inputs[m[1]] = points;
    });
    settings[headMatch[1]] = { base: Number(headMatch[2]), inputs };
  });
  return { version, name: '', parent: '', settings };
}

function mapInput(points, x) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i += 1) {
    if (x <= points[i][0]) {
      const span = Math.max(1e-6, points[i][0] - points[i - 1][0]);
      return points[i - 1][1] + (points[i][1] - points[i - 1][1]) * ((x - points[i - 1][0]) / span);
    }
  }
  return points[points.length - 1][1];
}

function setting(brush, name, defaultBase) {
  const entry = brush.settings[name];
  return entry ? entry : { base: defaultBase, inputs: {} };
}

function valueAt(entry, inputs) {
  let value = entry.base;
  Object.keys(entry.inputs).forEach((key) => {
    if (key in inputs) value += mapInput(entry.inputs[key], inputs[key]);
  });
  return value;
}

const SUPPORTED_INPUTS = new Set(['pressure', 'random']);

export function translateMyPaintBrush(brush, context = {}) {
  const report = { implemented: ['procedural round dab'], approximated: [], missing: [], notes: [] };
  const radius = setting(brush, 'radius_logarithmic', 2);
  const opaque = setting(brush, 'opaque', 1);
  const opaqueMultiply = setting(brush, 'opaque_multiply', 0);
  const hardness = clamp(num(setting(brush, 'hardness', 0.8).base, 0.8), 0, 1);

  const usedInputs = new Set();
  ['radius_logarithmic', 'opaque', 'opaque_multiply', 'hardness', 'offset_by_random', 'radius_by_random'].forEach((name) => {
    Object.keys(setting(brush, name, 0).inputs).forEach((input) => usedInputs.add(input));
  });
  usedInputs.forEach((input) => {
    if (!SUPPORTED_INPUTS.has(input)) report.approximated.push(`input "${input}" evaluated at its resting value`);
  });

  const sizeCurve = [];
  const hasRadiusPressure = !!radius.inputs.pressure;
  if (hasRadiusPressure) {
    const top = Math.exp(valueAt(radius, { pressure: 1 }));
    for (let i = 0; i <= 32; i += 1) {
      const p = i / 32;
      sizeCurve.push({ x: p, y: clamp(Math.exp(valueAt(radius, { pressure: p })) / Math.max(1e-6, top), 0, 1) });
    }
    report.implemented.push('pressure → size');
  }

  const opaqueAt = (p) => clamp(valueAt(opaque, { pressure: p }), 0, 1) * clamp(Object.keys(opaqueMultiply.inputs).length || opaqueMultiply.base ? valueAt(opaqueMultiply, { pressure: p }) : 1, 0, 1);
  const opacityTop = Math.max(1e-6, opaqueAt(1));
  const hasOpacityPressure = !!(opaque.inputs.pressure || opaqueMultiply.inputs.pressure);
  const opacityCurve = [];
  if (hasOpacityPressure) {
    for (let i = 0; i <= 32; i += 1) {
      const p = i / 32;
      opacityCurve.push({ x: p, y: clamp(opaqueAt(p) / opacityTop, 0, 1) });
    }
    report.implemented.push('pressure → opacity');
  }

  const perActual = Math.max(0, num(setting(brush, 'dabs_per_actual_radius', 2).base, 2));
  const perBasic = Math.max(0, num(setting(brush, 'dabs_per_basic_radius', 0).base, 0));
  const spacing = clamp(0.5 / Math.max(0.25, perActual + perBasic), 0.02, 2);
  if (num(setting(brush, 'dabs_per_second', 0).base) > 0) report.missing.push('dabs per second (time-based dabs)');
  if (perBasic > 0) report.approximated.push('dabs per basic radius folded into spacing');

  const offsetRandom = clamp(num(setting(brush, 'offset_by_random', 0).base), 0, 8);
  const radiusRandom = clamp(num(setting(brush, 'radius_by_random', 0).base), 0, 4);
  if (offsetRandom > 0.001) report.implemented.push('random offset');
  if (radiusRandom > 0.001) report.approximated.push('random radius as size jitter');

  const ratio = Math.max(1, num(setting(brush, 'elliptical_dab_ratio', 1).base, 1));
  const ellipseAngle = setting(brush, 'elliptical_dab_angle', 90);
  const followDirection = !!(ellipseAngle.inputs.direction || ellipseAngle.inputs.direction_360);
  if (ratio > 1.01) report.implemented.push('elliptical dab');
  if (followDirection) report.implemented.push('dab follows stroke direction');

  const smudge = num(setting(brush, 'smudge', 0).base);
  if (smudge > 0.01 || Object.keys(setting(brush, 'smudge', 0).inputs).length) {
    report.missing.push({ label: 'smudge (colour pickup and smearing)', major: true });
  }
  const eraser = num(setting(brush, 'eraser', 0).base) > 0.5;
  if (eraser) report.implemented.push('eraser');
  [['lock_alpha', 'lock alpha'], ['colorize', 'colorize'], ['posterize', 'posterize'], ['snap_to_pixel', 'snap to pixel'], ['slow_tracking', 'slow tracking'], ['tracking_noise', 'tracking noise'], ['custom_input', 'custom input'], ['stroke_holdtime', 'stroke hold time'], ['direction_filter', 'direction filter']].forEach(([key, label]) => {
    const entry = brush.settings[key];
    if (entry && ((Math.abs(entry.base) > 0.001 && !['slow_tracking', 'direction_filter'].includes(key)) || Object.keys(entry.inputs).length)) report.missing.push(label);
  });
  if (brush.settings.slow_tracking && brush.settings.slow_tracking.base > 0.5) report.approximated.push('slow tracking uses Voxemera smoothing');

  const colourJitter = { hue: 0, saturation: 0, lightness: 0, darkness: 0 };
  [['change_color_h', 'hue'], ['change_color_hsv_s', 'saturation'], ['change_color_l', 'lightness']].forEach(([key, target]) => {
    const entry = brush.settings[key];
    if (!entry) return;
    if (entry.inputs.random) {
      const points = entry.inputs.random;
      colourJitter[target] = clamp(Math.max(...points.map((p) => Math.abs(p[1]))), 0, 1);
      report.approximated.push(`${target} random change as jitter`);
    } else if (Math.abs(entry.base) > 0.001 || Object.keys(entry.inputs).length) {
      report.missing.push(`${target} colour change`);
    }
  });

  report.approximated.push('MyPaint opacity linearisation (opaque_linearize) approximated by build-up dabs');

  const baseRadius = Math.exp(num(radius.base, 2));
  return {
    name: context.name || brush.name || '',
    proceduralTip: { kind: 'round', hardness, antialias: true },
    settings: {
      spacing,
      tipScale: 1,
      angle: ((num(ellipseAngle.base, 90) - 90) * Math.PI) / 180,
      roundness: clamp(1 / ratio, 0.02, 1),
      opacity: 1,
      flow: clamp(opaqueAt(1), 0.005, 1),
      opacityMode: 'dab',
      eraser,
      edgeMode: 'normal',
      suggestedSize: clamp(baseRadius * 2, 1, 1000),
    },
    dynamics: {
      size: { pressure: { amount: hasRadiusPressure ? 1 : 0, curve: sizeCurve }, jitter: clamp(radiusRandom / 2, 0, 1) },
      opacity: { pressure: { amount: hasOpacityPressure ? 1 : 0, curve: opacityCurve } },
      rotation: { follow: followDirection ? 1 : 0 },
      scatter: { perpendicular: offsetRandom / 2, along: offsetRandom / 2 },
      colour: { perDab: colourJitter },
    },
    rendering: { mode: smudge > 0.01 ? 'smudge' : 'buildup' },
    compatibility: scoreCompatibility(report),
  };
}
